/**
 * MembershipRolePage — the Memberships role REFERENCE view, main pane
 * (Track 4 Slice C′; architect ratification 2026-07-09 on DEV-1425 —
 * supersedes the Slice-C MembershipTray after Skip's redline: a ~380px tray
 * could not carry a real team grid + rhythm panel).
 *
 * Routes:
 *   /memberships/role/:key    — one held role (key = the canonical engagement
 *                               key, URL-encoded; GUID-case-insensitive match;
 *                               unknown key falls back gracefully — ruling 1)
 *   (/memberships/life4earth RETIRED 2026-07-09 evening — redirects to the
 *   signature page; the Impact Signature card superseded the standing view.)
 *
 * Layout carried over from the Action Center role detail (consistent
 * grammar): hero banner (role, entity, community path, context action) →
 * standing (basis + since) → RoleSummary full-width (description → rhythm →
 * team) → quiet "Role options → Step down."
 *
 * §7.1/§7.5(c) bindings carry over VERBATIM: this is what the role IS —
 * reference prose and rhythms, never a task list, zero obligation rows.
 * Own-role resign is the sole action; no grant/assign/edit controls, ever.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import RoleSummary, {
  type RoleSummaryTeamMember,
} from "../../Components/RoleSummary/RoleSummary";
import type { SelectableEntry } from "../../Data/Engagement/engagementEntry";
import { fetchRoleSlots, type RoleSlotResponse } from "../../api/communityRoleSlots";
import { resignFromCommunityRoleSlot } from "../../api/communityRoleSlotInvitations";
import { resignFromMiniProjectRole } from "../../api/miniProjectRoles";
import { updateRoleAssignment } from "../../api/projectRoles";
import { permissionsApiV2 } from "../../security/v2/apiClient";
import { invalidateByPrefix } from "../../api/queryCache";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { resolveCommunityPath } from "../../Data/ProjectConfig/projectConfigOverrides";
import styles from "./MembershipRolePage.module.css";

export interface MembershipRolePageProps {
  currentUserGuid: string;
  /** The shared composer's engagement entries (App-level; same set the rail
   *  and the Action Center use — one source). */
  engagementEntries: SelectableEntry[];
}

function initialsOf(name: string): string {
  return (name || "?")
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
}

/** Localized basis label with a safe fallback for unknown types. */
function basisKey(membershipType: string): string {
  const k = (membershipType || "").toLowerCase();
  if (k === "home" || k === "operating" || k === "joined" || k === "org_member") {
    return `memberships.tray.basis.${k}`;
  }
  return "memberships.tray.basis.other";
}

const MembershipRolePage: React.FC<MembershipRolePageProps> = ({
  currentUserGuid,
  engagementEntries,
}) => {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const { key: rawKey } = useParams<{ key: string }>();

  // ── Resolve the entry (GUID-case-insensitive per ruling 1) ──
  const decodedKey = useMemo(() => {
    if (!rawKey) return null;
    try {
      return decodeURIComponent(rawKey);
    } catch {
      return rawKey;
    }
  }, [rawKey]);
  const entry = useMemo(() => {
    if (!decodedKey) return null;
    const target = decodedKey.toUpperCase();
    return engagementEntries.find((e) => e.key.toUpperCase() === target) || null;
  }, [decodedKey, engagementEntries]);

  const entryTag = entry?.typeTag;
  const communityGuid = entry
    ? entry.groupCommunityGuid || entry.owningCommunityGuid || null
    : null;

  // ── Standing (basis + since) for the role's community ──
  const { communityMemberships, securityUsers } = useApiSecurityData();

  // guid → {name, avatar} for the team cards — same resolution the AC About
  // tab used (linked members' names/photos come from the security-users
  // list; the slot rows only carry GUIDs + figurehead manual fields).
  const teamUserInfo = useMemo(() => {
    const m = new Map<string, { name: string; avatar: string | null }>();
    for (const u of securityUsers || []) {
      const g = String(
        (u as any).user_guid || (u as any).userGuid || (u as any).guid || "",
      ).toUpperCase();
      if (!g) continue;
      m.set(g, {
        name:
          (u as any).display_name ||
          (u as any).displayName ||
          (u as any).name ||
          (u as any).email ||
          "",
        avatar: (u as any).profile_image_url || (u as any).profileImageUrl || null,
      });
    }
    return m;
  }, [securityUsers]);
  const membership = useMemo(() => {
    if (!communityGuid) return null;
    const me = String(currentUserGuid || "").toUpperCase();
    const guidUpper = communityGuid.toUpperCase();
    const mine = (communityMemberships || []).filter(
      (m: any) =>
        String(m.user_guid || "").toUpperCase() === me &&
        String(m.community_entity_guid || "").toUpperCase() === guidUpper &&
        String(m.status || "active").toLowerCase() === "active",
    );
    if (mine.length === 0) return null;
    const prio = ["home", "operating", "org_member", "joined"];
    const rank = (m: any) => {
      const i = prio.indexOf(String(m.membership_type || "").toLowerCase());
      return i < 0 ? 99 : i;
    };
    const top: any = [...mine].sort((a, b) => rank(a) - rank(b))[0];
    return {
      membershipType: String(top.membership_type || "joined"),
      since: (top.started_at as string) || null,
    };
  }, [communityGuid, communityMemberships, currentUserGuid]);

  const sinceLabel = useMemo(() => {
    if (!membership?.since) return null;
    const dt = new Date(membership.since);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }, [membership?.since]);

  // ── Team roster for community/org roles (same source the tray used) ──
  const [communitySlots, setCommunitySlots] = useState<RoleSlotResponse[]>([]);
  useEffect(() => {
    let cancelled = false;
    setCommunitySlots([]);
    if (!communityGuid) return;
    if (entryTag !== "community" && entryTag !== "organization") return;
    fetchRoleSlots(communityGuid)
      .then((slots) => {
        if (!cancelled) setCommunitySlots(slots || []);
      })
      .catch(() => {
        /* best-effort — team section just stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, [communityGuid, entryTag]);

  const teamMembers = useMemo<RoleSummaryTeamMember[] | undefined>(() => {
    if (entryTag !== "community" && entryTag !== "organization") return undefined;
    const me = String(currentUserGuid || "").toUpperCase();
    const out: RoleSummaryTeamMember[] = [];
    for (const slot of communitySlots) {
      if (slot.isHidden) continue;
      const oa = slot.openAssignment;
      const memberGuid =
        oa && oa.status === "active"
          ? oa.userGuid
          : !oa && slot.linkedUserGuid
            ? slot.linkedUserGuid
            : null;
      if (memberGuid) {
        const u = teamUserInfo.get(String(memberGuid).toUpperCase());
        out.push({
          key: `slot:${slot.roleSlotGuid}`,
          userGuid: memberGuid,
          name: u?.name || (oa as any)?.userDisplayName || "",
          avatar: u?.avatar || null,
          roleTitle: slot.displayTitle || "",
          isCurrentUser: String(memberGuid).toUpperCase() === me,
        });
      } else if (slot.manualName) {
        // Figurehead — a named team member with no L4E account.
        out.push({
          key: `fig:${slot.roleSlotGuid}`,
          userGuid: null,
          name: slot.manualName,
          avatar: slot.manualPhotoUrl || null,
          roleTitle: slot.displayTitle || slot.manualTitle || "",
          isCurrentUser: false,
          email: slot.manualEmail,
          profileUrl: slot.externalProfileUrl,
        });
      }
    }
    return out;
  }, [communitySlots, currentUserGuid, entryTag, teamUserInfo]);

  // ── Resign (quiet "Role options"; own-role only — §7.5c fence) ──
  const [resignOpen, setResignOpen] = useState(false);
  const [resignReason, setResignReason] = useState("");
  const [resigning, setResigning] = useState(false);
  const [resignError, setResignError] = useState<string | null>(null);
  useEffect(() => {
    setResignOpen(false);
    setResignReason("");
    setResignError(null);
  }, [entry?.key]);

  const resignInfo = useMemo<{ enabled: boolean; disabledReason: string | null } | null>(() => {
    if (!entry) return null;
    const src = entry.source;
    if (src.type === "workspace") return null; // own Workshop — nothing to resign
    if (entry.isPrevious) return null; // already ended
    if (src.type === "role") {
      const rc = (src.data as any)?.roleCategory;
      if (rc === "mini-project-lead")
        return { enabled: false, disabledReason: t("engagements.cannotResignLead") };
      if (rc === "mini-project-team")
        return { enabled: false, disabledReason: t("engagements.cannotResignTeamMember") };
      return { enabled: true, disabledReason: null };
    }
    if (src.type === "community") return { enabled: true, disabledReason: null };
    return null;
  }, [entry, t]);

  const handleResignConfirm = useCallback(async () => {
    if (!entry) return;
    const src = entry.source;
    setResigning(true);
    setResignError(null);
    try {
      if (src.type === "role") {
        const r = src.data as any;
        if (r.engagementType === "governance") {
          await permissionsApiV2.resignFromGrant(r.assignmentGuid);
        } else if (r.engagementType === "mini-project") {
          await resignFromMiniProjectRole(r.assignmentGuid, { reason: resignReason || null });
        } else {
          await updateRoleAssignment(r.projectGuid, r.roleSlotGuid, r.assignmentGuid, {
            status: "resigned",
            resignationReason: resignReason || undefined,
          });
        }
      } else if (src.type === "community") {
        const c = src.data as any;
        if (c.slotAssignmentGuid) {
          await resignFromCommunityRoleSlot(c.slotAssignmentGuid, { reason: resignReason || null });
        } else {
          // V1 membership fallback — hard-delete the user-direct V2 grant.
          const my = await permissionsApiV2.myGrants();
          const target = (my.directGrants || []).find(
            (g: any) =>
              String(g.scopeGuid).toUpperCase() === String(c.entityGuid).toUpperCase() &&
              String(g.scopeType).toLowerCase() === "community",
          );
          if (!target) throw new Error(t("engagements.resignErrorFallback"));
          await permissionsApiV2.resignFromGrant(target.grantGuid);
        }
      }
      invalidateByPrefix("communitySlotOccupancies:");
      invalidateByPrefix("communityPreviousRoles:");
      window.dispatchEvent(
        new CustomEvent("l4e_security_updated", { detail: { source: "memberships-resign" } }),
      );
      // The role no longer exists — return to the community (belonging home).
      navigate(communityGuid ? `/communities/${communityGuid}#feed` : "/communities");
    } catch (err) {
      setResignError(
        err instanceof Error && err.message ? err.message : t("engagements.resignErrorFallback"),
      );
    } finally {
      setResigning(false);
    }
  }, [entry, resignReason, t, navigate, communityGuid]);

  // ── Role reference inputs (same derivation the AC About tab used) ──
  const d = (entry?.source.data as any) || null;
  const sourceDefinitionGuid = (d && d.sourceDefinitionGuid) || null;
  const slotGuid = (d && d.roleSlotGuid) || null;
  const entityIsPerpetual = entryTag === "community" || entryTag === "organization";
  const description = entry
    ? entry.source.type === "role"
      ? d?.roleDescription || ""
      : entry.source.type === "community"
        ? t("engagements.communityMembershipDescription")
        : entry.source.type === "workspace"
          ? t("engagements.workspaceDescriptionDefault")
          : ""
    : "";

  // ── Unknown key → graceful fallback (ruling 1: never a crash, no
  //     redirect race while entries hydrate — a quiet state with a way home).
  if (!entry) {
    return (
      <div className={styles.fallback}>
        <div>{t("memberships.page.notFound")}</div>
        <button
          type="button"
          className={styles.fallbackBtn}
          /* DEV-1458 (§7.7): "Back to Memberships" now goes to the section's
             own home. It used to point at /communities — a leftover from the
             era when My Impact had no home route and borrowed the community
             browse page as its "way home". */
          onClick={() => navigate("/memberships")}
        >
          {t("memberships.page.backToMemberships")}
        </button>
      </div>
    );
  }

  const heroTitle = entry.roleLabel;
  const heroEntity = entry.entityName;
  const heroUrl = entry.heroUrl;
  const logoUrl = entry.logoUrl;
  // Slice C″ (2026-07-09 review §8.1): the hero declares what KIND of role
  // this is — project / organization / civic — via a text tag (shape + label
  // carry the meaning; the tint is reinforcement, never color alone).
  const roleType: "project" | "org" | "civic" =
    entryTag === "full-project" || entryTag === "mini-project"
      ? "project"
      : entry.pillar === "communities"
        ? "civic"
        : "org";

  return (
    <div className={styles.page}>
      {/* DEV-1452: in-app back button removed. */}
      {/* ── Hero banner (AC role-detail grammar) ── */}
      <div
        className={styles.roleBanner}
        style={
          heroUrl
            ? {
                backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%), url(${heroUrl})`,
              }
            : undefined
        }
      >
        <div className={styles.roleBannerContent}>
          <div className={styles.roleBannerRow}>
            <div className={styles.roleTitlesBlock}>
              {logoUrl ? (
                <img className={styles.roleAvatar} src={logoUrl} alt="" />
              ) : (
                <div className={styles.roleAvatarFallback} aria-hidden="true">
                  {initialsOf(heroEntity || heroTitle)}
                </div>
              )}
              <div className={styles.roleTitles}>
                <div className={styles.roleBannerTitle}>{heroTitle}</div>
                <div className={styles.roleBannerEntity}>{heroEntity}</div>
                <span
                  className={`${styles.roleTypeTag} ${
                    roleType === "project"
                      ? styles.roleTypeProject
                      : roleType === "civic"
                        ? styles.roleTypeCivic
                        : roleType === "org"
                          ? styles.roleTypeOrg
                          : ""
                  }`}
                >
                  {t(`memberships.roleType.${roleType}`)}
                </span>
                {(() => {
                    const path = communityGuid ? resolveCommunityPath(communityGuid) : [];
                    if (path.length > 0) {
                      return (
                        <div className={styles.roleBannerPath}>
                          {path.map((hop, i) => (
                            <React.Fragment key={hop.guid}>
                              {i > 0 ? (
                                <span className={styles.roleBannerPathSep} aria-hidden="true">
                                  {" / "}
                                </span>
                              ) : null}
                              <button
                                type="button"
                                className={styles.roleBannerPathLink}
                                onClick={() => navigate(`/communities/${hop.guid}`)}
                              >
                                {hop.name}
                              </button>
                            </React.Fragment>
                          ))}
                        </div>
                      );
                    }
                    return entry.secondary ? (
                      <div className={styles.roleBannerPath}>{entry.secondary}</div>
                    ) : null;
                  })()}
              </div>
            </div>
            {(() => {
                // Context-aware action (carried from the AC banner): project
                // roles open the PROJECT, community/org roles the community.
                const tag = entry.typeTag;
                let label: string | null = null;
                let onClick: (() => void) | null = null;
                if (tag === "full-project" || tag === "mini-project") {
                  const guid = d?._routeGuid || d?.projectGuid;
                  if (guid) {
                    label = t("actionCenter.inbox.roleTab.viewProject");
                    onClick = () =>
                      navigate(
                        tag === "mini-project" ? `/projects/${guid}/management` : `/projects/${guid}`,
                      );
                  }
                } else if (tag === "community" || tag === "organization") {
                  const guid = d?.entityGuid || communityGuid;
                  if (guid) {
                    label = t("actionCenter.inbox.roleTab.openCommunity");
                    onClick = () => navigate(`/communities/${guid}`);
                  }
                } else if (entry.source.type === "workspace") {
                  const wg = d?.workspaceGuid;
                  const cg = d?.communityGuid;
                  if (wg) {
                    label = t("actionCenter.inbox.roleTab.viewWorkspace");
                    onClick = () =>
                      navigate(cg ? `/communities/${cg}/workspaces/${wg}` : `/workspaces/${wg}`);
                  }
                }
                return (
                  <div className={styles.roleBannerActions}>
                    {/* Slice C″ — the REVERSE bridge (review priority 5): from
                        the reference view to this role's obligations, filtered.
                        Mirrors the AC's "View role in Memberships". */}
                    <button
                      type="button"
                      className={styles.roleBannerBtnGhost}
                      onClick={() =>
                        navigate(`/actions?role=${encodeURIComponent(entry.key)}`)
                      }
                    >
                      {t("memberships.page.tasksInActionCenter")}
                    </button>
                    {label && onClick ? (
                      <button type="button" className={styles.roleBannerBtn} onClick={onClick}>
                        {label}
                      </button>
                    ) : null}
                  </div>
                );
              })()}
          </div>
        </div>
      </div>

      <div className={styles.body}>
        {/* ── Standing (belonging — basis + since) ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t("memberships.tray.standingLabel")}</div>
          <div className={styles.sectionText}>
            {membership
              ? t(basisKey(membership.membershipType))
              : t("memberships.tray.basis.other")}
            {sinceLabel ? (
              <span className={styles.since}>
                {" · "}
                {t("memberships.tray.memberSince", { date: sinceLabel })}
              </span>
            ) : null}
          </div>
        </div>

        {/* ── The role — reference view via the shared RoleSummary ── */}
        {entry ? (
          <div className={styles.roleBlock}>
            <RoleSummary
              variant="full"
              roleLabel={entry.roleLabel}
              description={description}
              sourceDefinitionGuid={sourceDefinitionGuid}
              slotGuid={slotGuid}
              entityIsPerpetual={entityIsPerpetual}
              teamMembers={teamMembers}
            />
          </div>
        ) : null}

        {/* ── Quiet "Role options" — own-role resign, nothing else, ever ── */}
        {entry && resignInfo ? (
          <div className={styles.roleOptions}>
            <div className={styles.roleOptionsLabel}>{t("memberships.tray.roleOptions")}</div>
            {resignOpen ? (
              <div className={styles.resignPanel}>
                <textarea
                  className={styles.resignReason}
                  placeholder={t("actionCenter.inbox.roleTab.resignReasonPlaceholder")}
                  value={resignReason}
                  onChange={(ev) => setResignReason(ev.target.value)}
                  rows={3}
                  disabled={resigning}
                />
                {resignError ? <div className={styles.resignError}>{resignError}</div> : null}
                <div className={styles.resignActions}>
                  <button
                    type="button"
                    className={styles.resignCancel}
                    onClick={() => setResignOpen(false)}
                    disabled={resigning}
                  >
                    {t("actionCenter.inbox.roleTab.resignCancel")}
                  </button>
                  <button
                    type="button"
                    className={styles.resignConfirm}
                    onClick={handleResignConfirm}
                    disabled={resigning}
                  >
                    {resigning
                      ? t("actionCenter.inbox.roleTab.resigning")
                      : t("actionCenter.inbox.roleTab.resignConfirm")}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={styles.stepDown}
                onClick={() => {
                  setResignReason("");
                  setResignError(null);
                  setResignOpen(true);
                }}
                disabled={!resignInfo.enabled}
                title={resignInfo.disabledReason || undefined}
              >
                {t("memberships.tray.stepDown", { role: entry.roleLabel })}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MembershipRolePage;
